import React, { CSSProperties, PropsWithChildren, useEffect, useMemo, useRef, useState } from 'react'

interface PropsInterface extends React.HTMLAttributes<HTMLDivElement> {
    isOpen: boolean
    duration?: number
    offset?: number
    direction?: 'down' | 'up'
    keepMounted?: boolean
    easing?: string
    'data-testid'?: string
}

const defaultDuration = 220
const defaultOffset = 16

/**
 *
 */
const SlideTransition = ({
    isOpen,
    duration = defaultDuration,
    offset = defaultOffset,
    direction = 'down',
    keepMounted = false,
    easing = 'ease',
    children,
    style,
    ...props
}: PropsWithChildren<PropsInterface>): JSX.Element => {
    const contentRef = useRef<HTMLDivElement>(null)
    const enterTimerRef = useRef<number>()
    const exitTimerRef = useRef<number>()
    const [isMounted, setIsMounted] = useState(isOpen)
    const [isVisible, setIsVisible] = useState(false)
    const [contentHeight, setContentHeight] = useState(0)

    useEffect(() => {
        return () => {
            window.clearTimeout(enterTimerRef.current)
            window.clearTimeout(exitTimerRef.current)
        }
    }, [])

    useEffect(() => {
        if (!isMounted) {
            return undefined
        }

        const setHeight = () => {
            setContentHeight(contentRef.current?.scrollHeight || 0)
        }

        setHeight()

        if (typeof ResizeObserver === 'undefined' || !contentRef.current) {
            return undefined
        }

        const observer = new ResizeObserver(() => setHeight())
        observer.observe(contentRef.current)

        return () => observer.disconnect()
    }, [children, isMounted])

    useEffect(() => {
        window.clearTimeout(enterTimerRef.current)
        window.clearTimeout(exitTimerRef.current)

        if (isOpen) {
            setIsMounted(true)
            enterTimerRef.current = window.setTimeout(() => {
                setIsVisible(true)
            }, 16)
            return undefined
        }

        setIsVisible(false)

        if (keepMounted) {
            return undefined
        }

        exitTimerRef.current = window.setTimeout(() => {
            setIsMounted(false)
        }, duration)

        return undefined
    }, [duration, isOpen, keepMounted])

    const closedTransformOffset = direction === 'down' ? -offset : offset

    const wrapperStyle = useMemo<CSSProperties>(() => ({
        overflow: 'hidden',
        maxHeight: isVisible ? `${contentHeight}px` : '0px',
        opacity: isVisible ? 1 : 0,
        transform: `translateY(${isVisible ? 0 : closedTransformOffset}px)`,
        transition: [
            `max-height ${duration}ms ${easing}`,
            `opacity ${duration}ms ${easing}`,
            `transform ${duration}ms ${easing}`
        ].join(', '),
        willChange: 'max-height, opacity, transform',
        pointerEvents: isVisible ? 'auto' : 'none',
        ...style
    }), [closedTransformOffset, contentHeight, duration, easing, isVisible, style])

    if (!isMounted && !keepMounted) {
        return null
    }

    return <div {...props} aria-hidden={!isVisible} style={wrapperStyle}>
        <div ref={contentRef}>
            {children}
        </div>
    </div>
}

/**
 *
 */
export type SlideTransitionType = PropsInterface

export default SlideTransition
